import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Megaphone, Inbox as InboxIcon, Plus, Mail, CheckCircle2, AlertTriangle } from "lucide-react";
import api from "../lib/api";

export default function Deps() {
  const [tab, setTab] = useState("sent");        // sent | inbox
  const [sent, setSent] = useState([]);
  const [inbox, setInbox] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      api.get("/deps/broadcasts").then(({ data }) => data).catch(() => []),
      api.get("/deps/inbox").then(({ data }) => data).catch(() => []),
    ]).then(([s, i]) => {
      if (cancelled) return;
      setSent(s); setInbox(i);
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="space-y-5" data-testid="deps-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="label-overline">Deps</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Dep broadcasts</h1>
          <p className="text-ink-600 mt-1 text-sm">Mass-email a dep request to AMDS players and your own contacts. Replies come back to your inbox.</p>
        </div>
        <Link to="/deps/new" className="btn-primary" data-testid="dep-new-btn">
          <Plus size={16} /> New broadcast
        </Link>
      </div>

      <div className="flex gap-1 p-1 bg-ivory-100 rounded-full w-fit" data-testid="deps-tabs">
        {[
          { v: "sent", label: "Sent", icon: Megaphone, count: sent.length },
          { v: "inbox", label: "Inbox", icon: InboxIcon, count: inbox.length },
        ].map((t) => (
          <button key={t.v} onClick={() => setTab(t.v)}
            data-testid={`deps-tab-${t.v}`}
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium transition ${
              tab === t.v ? "bg-sage-500 text-white shadow-soft" : "text-ink-700 hover:bg-white/70"
            }`}>
            <t.icon size={14} /> {t.label}
            <span className={`text-xs ${tab === t.v ? "text-white/80" : "text-ink-500"}`}>
              {loading ? "–" : t.count}
            </span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="card-base p-6 text-ink-600">Loading…</div>
      ) : tab === "sent" ? (
        sent.length === 0 ? (
          <div className="card-base p-6 text-ink-600" data-testid="sent-empty">
            You haven't broadcast a dep yet. <Link to="/deps/new" className="text-sage-600 underline">Send your first one</Link>.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            {sent.map((b) => (
              <Link key={b.id} to={`/deps/${b.id}`} className="card-base p-4 hover:-translate-y-[1px] transition" data-testid={`sent-row-${b.id}`}>
                <div className="flex items-center gap-2 text-sm text-ink-600">
                  <Megaphone size={14} className="text-sage-500" />
                  <span>{b.event_type.replace("_", " ")}</span>
                  <span>·</span>
                  <span>{b.date}</span>
                </div>
                <div className="font-heading font-semibold mt-1">{b.instrument}</div>
                {b.venue && <div className="text-sm text-ink-600 truncate">{b.venue}</div>}
                <div className="flex items-center gap-3 mt-2 text-xs text-ink-600">
                  <span className="inline-flex items-center gap-1 text-sage-700"><CheckCircle2 size={12} /> {b.sent} sent</span>
                  {b.failed > 0 && (
                    <span className="inline-flex items-center gap-1 text-terracotta-700"><AlertTriangle size={12} /> {b.failed} failed</span>
                  )}
                  <span>·</span>
                  <span>{b.total} total</span>
                </div>
              </Link>
            ))}
          </div>
        )
      ) : (
        inbox.length === 0 ? (
          <div className="card-base p-6 text-ink-600" data-testid="inbox-empty">
            No deps to cover right now. Your diary will show new ones the moment they're sent.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            {inbox.map((b) => (
              <Link key={b.id} to={`/deps/${b.id}`} className="card-base p-4 hover:-translate-y-[1px] transition" data-testid={`inbox-row-${b.id}`}>
                <div className="flex items-center gap-2 text-sm text-ink-600">
                  <Mail size={14} className="text-sage-500" />
                  <span>{b.from_name}</span>
                </div>
                <div className="font-heading font-semibold mt-1">{b.instrument} · {b.event_type.replace("_", " ")}</div>
                <div className="text-sm text-ink-600">{b.date}{b.call_time ? ` · ${b.call_time}` : ""}</div>
                {b.venue && <div className="text-xs text-ink-500 mt-1 truncate">{b.venue}</div>}
              </Link>
            ))}
          </div>
        )
      )}
    </div>
  );
}
